/**
 * Product Variant Selector
 * Compatible con:
 * - Main Product
 * - Quick Add Modal
 * Escalable a N opciones
 */

class ProductVariantSelector {
  constructor(container, productData, callbacks = {}) {
    this.container = container;
    this.product = productData;
    this.variants = productData.variants;
    this.options = productData.options;
    this.callbacks = callbacks;

    this.optionIndices = {};
    this.selectedOptions = {};
    this.currentVariant = null;

    this.init();
  }

  /* ---------------- INIT ---------------- */

  init() {
    this.mapOptionIndices();
    this.initDefaultSelection();
    this.bindEvents();
    this.refreshUI();
  }

  mapOptionIndices() {
    this.options.forEach((name, index) => {
      this.optionIndices[name] = index;
    });
  }

  initDefaultSelection() {
    // Buscar la primera variante disponible
    const firstAvailable = this.variants.find(v => {
      const isMarkedAvailable = v.available === true || v.available === 1;
      const tracksInventory = v.inventory_management != null;
      const hasInventory = v.inventory_quantity != null && v.inventory_quantity > 0;
      const inventoryPolicy = v.inventory_policy || 'deny';
      const canSellWithoutStock = inventoryPolicy === 'continue';
      
      // Si no se rastrea inventario, confiar solo en 'available'
      if (!tracksInventory) {
        return isMarkedAvailable;
      }
      
      // Si se rastrea inventario, considerar disponible si tiene stock o puede vender sin stock
      return hasInventory || (canSellWithoutStock && isMarkedAvailable) || isMarkedAvailable;
    });
    
    // Si no hay ninguna disponible, usar la primera variante de todas formas
    const variantToUse = firstAvailable || this.variants[0];

    this.options.forEach((name, index) => {
      this.selectedOptions[name] = variantToUse[`option${index + 1}`];
    });

    this.currentVariant = variantToUse;
  }

  /* ------------- CORE LOGIC ------------- */

  getMatchingVariant() {
    return this.variants.find(variant =>
      this.options.every((name, index) => {
        return (
          variant[`option${index + 1}`] === this.selectedOptions[name]
        );
      })
    );
  }

  getSelectedVariantId() {
    if (this.currentVariant && this.currentVariant.id) {
      return this.currentVariant.id;
    }
    // Si no hay currentVariant, intentar obtenerlo
    const matchingVariant = this.getMatchingVariant();
    if (matchingVariant && matchingVariant.id) {
      return matchingVariant.id;
    }
    return null;
  }

  isOptionAvailable(optionName, optionValue) {
    const optionIndex = this.optionIndices[optionName];

    // Verificar si hay alguna variante disponible con este valor de opción
    // IMPORTANTE: Ignoramos la selección actual de esta opción y verificamos si existe alguna
    // variante disponible que tenga este valor, respetando SOLO las otras selecciones
    
    // Primero, contar cuántas otras opciones están seleccionadas
    const otherSelectedCount = this.options.filter((name, idx) => 
      idx !== optionIndex && this.selectedOptions[name] != null
    ).length;
    
    const hasAvailable = this.variants.some(variant => {
      // Primero verificar que la variante tenga el valor correcto para esta opción
      if (variant[`option${optionIndex + 1}`] !== optionValue) {
        return false;
      }
      
      // Verificar que la variante esté disponible
      // Shopify tiene diferentes políticas de inventario:
      // 1. Si inventory_management es null, no se rastrea inventario - confiar en 'available'
      // 2. Si inventory_quantity es null o no existe, puede ser que no se rastree o esté agotado
      // 3. Si inventory_policy es 'continue', puede seguir vendiendo aunque inventory_quantity sea 0
      
      const isMarkedAvailable = variant.available === true || variant.available === 1;
      const hasInventoryQuantity = variant.inventory_quantity != null;
      const hasInventory = hasInventoryQuantity && variant.inventory_quantity > 0;
      const inventoryPolicy = variant.inventory_policy || 'deny';
      const tracksInventory = variant.inventory_management != null;
      
      // Si no se rastrea inventario, confiar solo en 'available'
      if (!tracksInventory) {
        if (!isMarkedAvailable) {
          return false;
        }
      } else {
        // Si se rastrea inventario:
        // - Si tiene inventario > 0, está disponible
        // - Si tiene inventory_policy 'continue', puede estar disponible aunque inventory_quantity sea 0
        // - Si está marcado como available y no hay inventory_quantity definido, considerar disponible
        const canSellWithoutStock = inventoryPolicy === 'continue';
        const hasStock = hasInventory || (canSellWithoutStock && isMarkedAvailable);
        
        if (!hasStock && !isMarkedAvailable) {
          return false;
        }
      }
      
      // Si no hay otras selecciones, cualquier variante disponible con este valor es válida
      if (otherSelectedCount === 0) {
        return true;
      }
      
      // Si hay otras opciones seleccionadas, verificar que coincidan
      for (let i = 0; i < this.options.length; i++) {
        if (i === optionIndex) {
          continue; // Ignorar esta opción - estamos verificando este valor específico
        }
        const otherOptionName = this.options[i];
        const selectedValue = this.selectedOptions[otherOptionName];
        // Si hay una selección para otra opción, debe coincidir
        if (selectedValue != null && variant[`option${i + 1}`] !== selectedValue) {
          return false;
        }
      }
      
      return true;
    });
    
    return hasAvailable;
  }

  /* ------------- UI UPDATE -------------- */

  refreshUI() {
    this.updateButtons();
    this.currentVariant = this.getMatchingVariant();
    this.updateHiddenInput();
    this.notifyVariantChange();
  }

  updateButtons() {
    const buttons = this.container.querySelectorAll('[data-option-name]');
    let availableCount = 0;
    
    // Contar variantes disponibles para debugging
    const totalVariantsAvailable = this.variants.filter(v => {
      const isMarkedAvailable = v.available === true || v.available === 1;
      const tracksInventory = v.inventory_management != null;
      const hasInventory = v.inventory_quantity != null && v.inventory_quantity > 0;
      const inventoryPolicy = v.inventory_policy || 'deny';
      const canSellWithoutStock = inventoryPolicy === 'continue';
      
      if (!tracksInventory) {
        return isMarkedAvailable;
      }
      
      return hasInventory || (canSellWithoutStock && isMarkedAvailable) || isMarkedAvailable;
    }).length;

    buttons.forEach(btn => {
      const optionName = btn.dataset.optionName;
      const optionValue = btn.dataset.optionValue;

      const available = this.isOptionAvailable(optionName, optionValue);

      // Actualizar estado del botón
      if (!available) {
        btn.disabled = true;
        btn.classList.add('disabled');
    } else {
        btn.disabled = false;
        btn.classList.remove('disabled');
        availableCount++;
      }
        
        // Actualizar estado activo
      btn.classList.toggle(
        'active',
        this.selectedOptions[optionName] === optionValue
      );
    });
    
  }

  updateHiddenInput() {
    const form = this.container.closest('form');
    if (!form || !this.currentVariant) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = this.currentVariant.id;
    }
  }

  notifyVariantChange() {
    if (!this.currentVariant) return;

    if (this.callbacks.onVariantChange) {
      this.callbacks.onVariantChange(this.currentVariant);
    }
    
    // Actualizar imagen si existe
    if (this.callbacks.onImageUpdate && this.currentVariant.featured_image) {
      let imageUrl = this.currentVariant.featured_image;
      if (typeof imageUrl === 'object' && imageUrl.src) {
        imageUrl = imageUrl.src;
      }
      // Asegurar protocolo completo
      if (imageUrl && imageUrl.startsWith('//')) {
        imageUrl = 'https:' + imageUrl;
      }
      this.callbacks.onImageUpdate(imageUrl);
    }
    
    // Actualizar precio
    if (this.callbacks.onPriceUpdate) {
      this.callbacks.onPriceUpdate(this.currentVariant);
    }
  }

  /* ---------------- EVENTS -------------- */

  bindEvents() {
    this.container.addEventListener('click', e => {
      // Solo procesar clicks en botones del selector de variantes
      // Usar closest para encontrar el botón, pero verificar que esté dentro del contenedor
      const btn = e.target.closest('[data-option-name]');
      if (!btn || !this.container.contains(btn)) return;

      const optionName = btn.dataset.optionName;
      const optionValue = btn.dataset.optionValue;

      if (!this.isOptionAvailable(optionName, optionValue)) return;
      
      // Prevenir propagación solo después de verificar que es un botón válido
      // Esto no debería interferir con otros clicks (como zoom de imágenes) porque
      // solo se ejecuta cuando el click es en un botón del selector
      e.stopPropagation();

      this.selectedOptions[optionName] = optionValue;
      this.refreshUI();
    });
  }
}

/* -------- QUICK ADD OPTION RENDER -------- */

function renderQuickAddOptions(container, product) {
  const rendered = container.dataset.rendered === 'true';
  if (rendered) return;

  product.options.forEach((optionName, index) => {
    const values = [
      ...new Set(
        product.variants.map(v => v[`option${index + 1}`])
      )
    ];

    let target = null;
    let parentContainer = null;
    const lower = optionName.toLowerCase();

    if (lower.includes('color')) {
      target = container.querySelector('.product-variant-selector__color-buttons');
      parentContainer = container.querySelector('.product-variant-selector__colors');
    } else if (lower.includes('talla') || lower.includes('size')) {
      parentContainer = container.querySelector('.product-variant-selector__sizes');
      target = parentContainer ? parentContainer.querySelector('.product-variant-selector__size-buttons') : null;
    } else if (lower.includes('fit')) {
      parentContainer = container.querySelector('.product-variant-selector__fit');
      target = parentContainer ? parentContainer.querySelector('.product-variant-selector__fit-buttons') : null;
    }

    if (!target || !parentContainer) return;
    
    // No agregar labels - se eliminaron según diseño

    values.forEach(value => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.dataset.optionName = optionName;
      btn.dataset.optionValue = value;

      if (lower.includes('color')) {
        btn.className = 'product-variant-selector__color-button';
        
        // Buscar la primera variante con este valor de color para obtener su imagen
        const variantWithColor = product.variants.find(v => v[`option${index + 1}`] === value);
        
        if (variantWithColor && variantWithColor.featured_image) {
          // Crear elemento de imagen
          const img = document.createElement('img');
          img.className = 'product-variant-selector__color-image';
          img.alt = value;
          
          // Obtener la URL de la imagen
          let imageUrl = variantWithColor.featured_image;
          if (typeof imageUrl === 'object') {
            imageUrl = imageUrl.src || imageUrl.url;
          }
          
          // Asegurar protocolo completo
          if (imageUrl && imageUrl.startsWith('//')) {
            imageUrl = 'https:' + imageUrl;
          }
          
          img.src = imageUrl;
          img.loading = 'lazy';
          
          btn.appendChild(img);
        } else {
          // Si no hay imagen, mostrar el texto como fallback
          btn.textContent = value;
        }
      } else if (lower.includes('fit')) {
        btn.className = 'product-variant-selector__fit-button';
        btn.textContent = value;
        } else {
        btn.className = 'product-variant-selector__size-button';
        btn.textContent = value;
      }

      target.appendChild(btn);
    });
    
    // Ocultar contenedores si no hay botones renderizados
    if (parentContainer) {
      parentContainer.classList.toggle('has-buttons', values.length > 0);
      if (values.length === 0) {
        parentContainer.style.display = 'none';
      } else {
        // Asegurar que esté visible si hay botones
        parentContainer.style.display = '';
      }
    }
  });
  
  // Verificar después de renderizar todos los botones
  setTimeout(() => {
    const colorContainer = container.querySelector('.product-variant-selector__colors');
    const colorButtons = container.querySelectorAll('.product-variant-selector__color-button');
    if (colorContainer) {
      if (colorButtons.length === 0) {
        colorContainer.style.display = 'none';
        colorContainer.classList.remove('has-buttons');
      } else {
        colorContainer.style.display = '';
        colorContainer.classList.add('has-buttons');
      }
    }

    const sizeContainer = container.querySelector('.product-variant-selector__sizes');
    const sizeButtons = container.querySelectorAll('.product-variant-selector__size-button');
    if (sizeContainer) {
      if (sizeButtons.length === 0) {
        sizeContainer.style.display = 'none';
        sizeContainer.classList.remove('has-buttons');
      } else {
        sizeContainer.style.display = '';
        sizeContainer.classList.add('has-buttons');
      }
    }
    
    const fitContainer = container.querySelector('.product-variant-selector__fit');
    const fitButtons = container.querySelectorAll('.product-variant-selector__fit-button');
    if (fitContainer) {
      if (fitButtons.length === 0) {
        fitContainer.style.display = 'none';
        fitContainer.classList.remove('has-buttons');
      } else {
        fitContainer.style.display = '';
        fitContainer.classList.add('has-buttons');
      }
    }
  }, 100);

  container.dataset.rendered = 'true';
}

/* -------- DROPDOWN-UP TRANSFORM (sticky bar mobile) -------- */

function transformToDropdownUp(container) {
  if (!container.classList.contains('product-variant-selector--dropdown-up')) return;
  if (container.dataset.dropdownTransformed === 'true') return;

  const groups = [
    {
      parent: container.querySelector('.product-variant-selector__colors'),
      buttons: container.querySelector('.product-variant-selector__color-buttons'),
      label: 'Color'
    },
    {
      parent: container.querySelector('.product-variant-selector__sizes'),
      buttons: container.querySelector('.product-variant-selector__size-buttons'),
      label: 'Talla'
    },
    {
      parent: container.querySelector('.product-variant-selector__fit'),
      buttons: container.querySelector('.product-variant-selector__fit-buttons'),
      label: 'Fit'
    }
  ];

  groups.forEach(function (g) {
    if (!g.parent || !g.buttons || !g.buttons.children.length) return;

    const labelEl = g.parent.querySelector('.product-variant-selector__label');
    const labelText = (labelEl && labelEl.textContent) ? labelEl.textContent.trim() : g.label;

    const dropdown = document.createElement('div');
    dropdown.className = 'variant-dropdown';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'variant-dropdown__trigger';
    trigger.innerHTML =
      '<span class="variant-dropdown__value"></span>' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 15l-6-6-6 6"/></svg>';

    const panel = document.createElement('div');
    panel.className = 'variant-dropdown__panel';
    panel.appendChild(g.buttons);

    dropdown.appendChild(trigger);
    dropdown.appendChild(panel);
    g.parent.appendChild(dropdown);

    function getActiveValue() {
      const active = g.buttons.querySelector('.active');
      if (active) {
        const img = active.querySelector('.product-variant-selector__color-image');
        return img ? active.dataset.optionValue || img.alt : (active.dataset.optionValue || active.textContent.trim());
      }
      const first = g.buttons.querySelector('[data-option-value]');
      return first ? (first.dataset.optionValue || first.textContent.trim()) : '-';
    }

    function updateTrigger() {
      let val = getActiveValue();
      if (val && val.length > 20) val = val.substring(0, 17) + '...';
      trigger.querySelector('.variant-dropdown__value').textContent = labelText + ' ' + (val || '-');
    }

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      dropdown.classList.toggle('is-open');
    });

    g.buttons.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-option-name]');
      if (btn) {
        requestAnimationFrame(function () {
          updateTrigger();
          dropdown.classList.remove('is-open');
        });
      }
    });

    document.addEventListener('click', function closeDropdown(e) {
      if (!dropdown.contains(e.target)) {
        dropdown.classList.remove('is-open');
      }
    });

    updateTrigger();
  });

  container.dataset.dropdownTransformed = 'true';
}

/* ------------ INIT GLOBAL -------------- */

function initVariantSelectors(context = document) {
  context
    .querySelectorAll('.product-variant-selector')
    .forEach(container => {
      const sectionId = container.dataset.sectionId;
      if (!sectionId) return;

      // Verificar si ya fue inicializado (por inicialización manual, por ejemplo en el modal)
      if (container.dataset.initialized === 'true') return;

      // El modal se inicializa manualmente desde product-card-quick-add.js
      // Solo inicializar si hay scripts JSON disponibles (página principal)
      const variantsScript = document.getElementById(
        `ProductVariants-${sectionId}`
      );
      const optionsScript = document.getElementById(
        `ProductOptions-${sectionId}`
      );
      
      // Si no hay scripts JSON, asumir que se inicializará manualmente (como en el modal)
      if (!variantsScript) return;

      const variants = JSON.parse(variantsScript.textContent);
      const options = optionsScript 
        ? JSON.parse(optionsScript.textContent)
        : container.dataset.productOptions 
          ? JSON.parse(container.dataset.productOptions) 
          : [];

      const productData = {
        variants: variants,
        options: options
      };

      // Limpiar contenido anterior si existe
      const colorButtons = container.querySelector('.product-variant-selector__color-buttons');
      const sizeButtons = container.querySelector('.product-variant-selector__size-buttons');
      const fitButtons = container.querySelector('.product-variant-selector__fit-buttons');
      if (colorButtons) colorButtons.innerHTML = '';
      if (sizeButtons) sizeButtons.innerHTML = '';
      if (fitButtons) fitButtons.innerHTML = '';
      
      container.dataset.rendered = 'false';

      renderQuickAddOptions(container, productData);

      // Detectar si estamos en el modal o en la página principal
      const isModal = container.closest('.product-quick-add-modal') !== null;
      const isMainProduct = !isModal;

      new ProductVariantSelector(container, productData, {
        onVariantChange(variant) {
          if (isModal) {
            // Callbacks para el modal
            const modal = container.closest('.product-quick-add-modal');
            if (!modal) return;

            const img = modal.querySelector('.product-quick-add-modal__image');
            if (variant.featured_image && img) {
              let imageUrl = variant.featured_image;
              if (typeof imageUrl === 'object' && imageUrl.src) {
                imageUrl = imageUrl.src;
              }
              if (imageUrl && imageUrl.startsWith('//')) {
                imageUrl = 'https:' + imageUrl;
              }
              img.src = imageUrl;
              img.style.opacity = '1';
            }

            const price = modal.querySelector('.product-quick-add-modal__price-current');
            if (price && window.Shopify?.formatMoney) {
              const currencyCode = window.Shopify?.currency?.active || window.currencyCode || 'CLP';
              price.textContent = `${Shopify.formatMoney(variant.price)} ${currencyCode}`;
            }
          } else if (isMainProduct) {
            // Callbacks para la página principal
            // Actualizar precio
            const sectionIdOnly = sectionId.replace('main-product-', '');
            const priceContainer = document.getElementById(`price-${sectionIdOnly}`);
            if (priceContainer && window.Shopify?.formatMoney) {
              const currentPrice = priceContainer.querySelector('.price-item--sale, .price-item--regular');
              if (currentPrice) {
                currentPrice.textContent = Shopify.formatMoney(variant.price);
              }
              
              // Actualizar precio comparado si existe
              if (variant.compare_at_price > variant.price) {
                let comparePrice = priceContainer.querySelector('.price-item--regular');
                if (!comparePrice) {
                  comparePrice = document.createElement('span');
                  comparePrice.className = 'price-item price-item--regular';
                  comparePrice.style.cssText = 'font-size: 1.6rem; text-decoration: line-through; color: #A9AEB2; font-weight: 400;';
                  priceContainer.appendChild(comparePrice);
                }
                comparePrice.textContent = Shopify.formatMoney(variant.compare_at_price);
                
                // Cambiar el precio actual a sale
                const salePrice = priceContainer.querySelector('.price-item--sale');
                if (!salePrice && currentPrice) {
                  currentPrice.className = 'price-item price-item--sale';
                  currentPrice.style.cssText = 'font-size: 2.6rem; font-weight: 700; color: #000000; line-height: 1;';
                }
              } else {
                // Eliminar precio comparado si no hay descuento
                const comparePrice = priceContainer.querySelector('.price-item--regular');
                if (comparePrice) comparePrice.remove();
                
                // Cambiar a precio regular
                if (currentPrice) {
                  currentPrice.className = 'price-item price-item--regular';
                  currentPrice.style.cssText = 'font-size: 2.6rem; font-weight: 700; color: #000000; line-height: 1;';
                }
              }
            }

            // Actualizar input hidden del formulario
            const productForm = document.getElementById(`product-form-${sectionIdOnly}`);
            if (productForm) {
              const idInput = productForm.querySelector('input[name="id"]');
              if (idInput) {
                idInput.value = variant.id;
              }
              
              // Actualizar botón de añadir al carrito
              const addButton = productForm.querySelector('[name="add"]');
              if (addButton) {
                const buttonText = addButton.querySelector('span');
                if (buttonText) {
                  if (variant.available) {
                    addButton.disabled = false;
                    if (productForm.closest('#product-buy-box-sticky') && window.Shopify?.formatMoney) {
                      var currencyCode = window.Shopify?.currency?.active || 'CLP';
                      buttonText.textContent = 'AÑADIR AL CARRO ' + Shopify.formatMoney(variant.price) + ' ' + currencyCode;
                    } else {
                      buttonText.textContent = 'AÑADIR AL CARRO';
                    }
                  } else {
                    addButton.disabled = true;
                    buttonText.textContent = 'AGOTADO';
                  }
                }
              }

              // Mostrar/ocultar botón de comprar ahora según disponibilidad
              const paymentButtonContainer = productForm.querySelector('.shopify-payment-button');
              if (paymentButtonContainer) {
                if (variant.available) {
                  paymentButtonContainer.style.display = '';
                  const paymentButton = paymentButtonContainer.querySelector('.shopify-payment-button__button, button');
                  if (paymentButton) {
                    paymentButton.removeAttribute('disabled');
                    paymentButton.removeAttribute('aria-disabled');
                  }
                } else {
                  paymentButtonContainer.style.display = 'none';
                  const paymentButton = paymentButtonContainer.querySelector('.shopify-payment-button__button, button');
                  if (paymentButton) {
                    paymentButton.setAttribute('disabled', 'disabled');
                    paymentButton.setAttribute('aria-disabled', 'true');
                  }
                }
              }
            }

            // Actualizar imagen/media si hay un selector de media
            // Esto se puede extender según necesidades específicas
            if (variant.featured_image) {
              // Disparar evento personalizado para que otros scripts puedan actualizar la imagen
              document.dispatchEvent(new CustomEvent('variant:change', {
                detail: { variant: variant }
              }));
            }
          }
        }
      });

      container.dataset.initialized = 'true';

      if (container.closest('#product-buy-box-sticky')) {
        setTimeout(function () {
          transformToDropdownUp(container);
        }, 150);
      }
    });
}

document.addEventListener('DOMContentLoaded', () => {
  initVariantSelectors();
});

document.addEventListener('shopify:section:load', e => {
  initVariantSelectors(e.target);
});

/* -------------- DEBUG ------------------ */
window.__VariantDebug = () => ({
  selectors: document.querySelectorAll('.product-variant-selector')
});
